import { useMutation, useQuery } from '@apollo/client/react';
import { useState } from 'react';
import { Globe } from 'react-bootstrap-icons';

import type { WeeklyAvailability } from '@repo/frontend-shared';
import {
  DataTestIds,
  DuplicateScheduleDocument,
  GetUserSchedulesDocument,
  SetPrimaryScheduleDocument,
} from '@repo/frontend-shared';
import { SDSSkeleton } from '@repo/sds/_internal/SDSSkeleton';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSCard } from '@repo/sds/SDSCard';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { SDSMenu, SDSMenuItem } from '@repo/sds/SDSMenu';
import { SDSSeparator } from '@repo/sds/SDSSeparator';
import { SDSText } from '@repo/sds/SDSText';
import { SDSTextBadge } from '@repo/sds/SDSTextBadge'; // Add this import
import { toast } from '@repo/sds/utils/toast';
import { useCurrentUser } from '@repo/ui/contexts/current-user-context';
import { getScheduleSummary } from '@repo/ui/utils/schedule-utils';

import { AvailabilityEditModal } from './availability-edit-modal';
import { DateOverridesSection } from './DateOverridesSection';
import './index.scss';

type SchedulesQueryData = { userWeeklyAvailabilities: WeeklyAvailability[] };

function handleScheduleSaveSuccess() {
  // Apollo cache updates keep the schedule list synchronized.
}

export function AvailabilityListPage() {
  const { currentUser } = useCurrentUser();
  const userId = currentUser?.id;
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);

  const { data, loading } = useQuery<SchedulesQueryData>(GetUserSchedulesDocument, {
    skip: !userId,
    variables: { userId },
    fetchPolicy: 'cache-and-network',
  });

  const [setPrimary] = useMutation<{ setPrimaryWeeklyAvailability: { id: string } }>(SetPrimaryScheduleDocument, {
    update: (cache, { data: mutationData }) => {
      if (!(mutationData && userId)) return;
      const existing = cache.readQuery<SchedulesQueryData>({ query: GetUserSchedulesDocument, variables: { userId } });
      if (!existing) return;
      const newPrimaryId = mutationData.setPrimaryWeeklyAvailability.id;
      cache.writeQuery({
        query: GetUserSchedulesDocument,
        variables: { userId },
        data: {
          __typename: 'Query' as const,
          userWeeklyAvailabilities: existing.userWeeklyAvailabilities.map(s => ({
            ...s,
            isPrimary: s.id === newPrimaryId,
          })),
        },
      });
    },
  });

  const [duplicateSchedule] = useMutation<{ duplicateWeeklyAvailability: WeeklyAvailability }>(
    DuplicateScheduleDocument,
    {
      update: (cache, { data: mutationData }) => {
        if (!(mutationData && userId)) return;
        const existing = cache.readQuery<SchedulesQueryData>({
          query: GetUserSchedulesDocument,
          variables: { userId },
        });
        if (!existing) return;
        cache.writeQuery({
          query: GetUserSchedulesDocument,
          variables: { userId },
          data: {
            __typename: 'Query' as const,
            userWeeklyAvailabilities: [...existing.userWeeklyAvailabilities, mutationData.duplicateWeeklyAvailability],
          },
        });
      },
    },
  );

  const schedules = data?.userWeeklyAvailabilities ?? [];

  const onSetDefault = async (id: string) => {
    try {
      await setPrimary({ variables: { id } });
      toast.success('Default schedule updated');
      setMenuOpenId(null);
    } catch {
      toast.error('Failed to set default schedule');
    }
  };

  const onDuplicate = async (schedule: WeeklyAvailability) => {
    try {
      const newTitle = `${schedule.title} (Copy)`;
      await duplicateSchedule({ variables: { id: schedule.id, newTitle } });
      toast.success(`Duplicated "${schedule.title}"`);
      setMenuOpenId(null);
    } catch {
      toast.error('Failed to duplicate schedule');
    }
  };

  const handleOpenModal = (scheduleId: string) => {
    setEditingScheduleId(scheduleId);
    setIsModalOpen(true);
    setMenuOpenId(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingScheduleId(null);
  };

  if (loading) {
    return (
      <SDSFlexbox direction="column" gap="content-large" className="SettingsAvailabilityList">
        <SDSFlexbox direction="column" gap="text-medium">
          <SDSText type="h5" text="Availability Settings" dataTestId={DataTestIds.ADMIN.SIDEBAR.PAGE_TITLE} />
          <SDSText type="body-medium" text="Configure times when you are available for bookings" />
        </SDSFlexbox>
        <SDSFlexbox direction="column" gap="card-large">
          {[1, 2, 3].map(i => (
            <SDSCard key={i}>
              <SDSFlexbox direction="row" justifyContent="space-between" alignItems="center">
                <SDSFlexbox direction="column" gap="text-small" className="AvailabilityListPage__content">
                  <SDSFlexbox direction="row" gap="text-small" alignItems="center">
                    <SDSSkeleton width={120} height={20} radius="small" />
                    <SDSSkeleton width={60} height={20} radius="full" />
                  </SDSFlexbox>
                  <SDSSkeleton width={200} height={16} radius="small" />
                  <SDSFlexbox direction="row" gap="text-small" alignItems="center">
                    <SDSSkeleton width={16} height={16} radius="small" />
                    <SDSSkeleton width={100} height={16} radius="small" />
                  </SDSFlexbox>
                </SDSFlexbox>
                <SDSSkeleton width={40} height={40} radius="full" />
              </SDSFlexbox>
            </SDSCard>
          ))}
        </SDSFlexbox>
        {userId && <DateOverridesSection userId={userId} />}
      </SDSFlexbox>
    );
  }

  if (schedules.length === 0) {
    return (
      <>
        <SDSFlexbox direction="column" gap="content-large" className="SettingsAvailabilityList">
          <SDSFlexbox direction="row" justifyContent="space-between" alignItems="flex-start">
            <SDSFlexbox direction="column" gap="text-medium">
              <SDSText type="h5" text="Availability Settings" dataTestId={DataTestIds.ADMIN.SIDEBAR.PAGE_TITLE} />
              <SDSText type="body-medium" text="Configure times when you are available for bookings" />
            </SDSFlexbox>
            <SDSButton
              mode="outline"
              text="+ New"
              onClick={() => handleOpenModal('new')}
              ariaLabel="Create new schedule"
            />
          </SDSFlexbox>

          <SDSFlexbox
            direction="column"
            alignItems="center"
            justifyContent="center"
            gap="content-default"
            className="AvailabilityListPage__empty-state"
          >
            <SDSText type="h5" text="No schedules yet" />
            <SDSText
              type="body-medium"
              text="Create your first availability schedule to let people know when you're available for meetings."
              color="inactive"
            />
          </SDSFlexbox>
        </SDSFlexbox>

        {userId && <DateOverridesSection userId={userId} />}

        <AvailabilityEditModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          scheduleId={editingScheduleId}
          onSaveSuccess={handleScheduleSaveSuccess}
        />
      </>
    );
  }

  return (
    <>
      <SDSFlexbox direction="column" gap="content-large" className="SettingsAvailabilityList">
        {/* Header with title and + New button */}
        <SDSFlexbox direction="row" justifyContent="space-between" alignItems="flex-start">
          <SDSFlexbox direction="column" gap="text-medium">
            <SDSText type="h5" text="Availability Settings" dataTestId={DataTestIds.ADMIN.SIDEBAR.PAGE_TITLE} />
            <SDSText type="body-medium" text="Configure times when you are available for bookings" />
          </SDSFlexbox>
          <SDSButton
            mode="outline"
            size="medium"
            text="+ New"
            onClick={() => handleOpenModal('new')}
            ariaLabel="Create new schedule"
          />
        </SDSFlexbox>

        {/* Horizontal separator under the header */}
        <SDSSeparator orientation="horizontal" />

        <SDSFlexbox direction="column" gap="card-large">
          {schedules.map((s: WeeklyAvailability, index) => (
            <div key={s.id}>
              <SDSFlexbox
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                className="AvailabilityListPage__schedule-item"
              >
                <SDSFlexbox direction="column" gap="text-small" className="AvailabilityListPage__content">
                  <SDSFlexbox direction="row" gap="text-small" alignItems="center">
                    <SDSText type="body-large-bold" text={s.title} />
                    {s.isPrimary && (
                      <SDSTextBadge
                        text="Default"
                        type="body-small"
                        icon={<div />} // Placeholder empty icon
                        color="primary"
                        size="small"
                        background="status-student"
                      />
                    )}
                  </SDSFlexbox>
                  <SDSText type="body-small" text={getScheduleSummary(s)} color="inactive" />
                  <SDSFlexbox direction="row" gap="text-small" alignItems="center">
                    <SDSIcon icon={<Globe />} color="inactive" />
                    <SDSText type="body-small" text={s.timezone} color="inactive" />
                  </SDSFlexbox>
                </SDSFlexbox>

                <SDSMenu
                  open={menuOpenId === s.id}
                  onOpenChange={(open: boolean) => setMenuOpenId(open ? s.id : null)}
                  trigger={
                    <SDSButton mode="outline" text="⋮" size="small" ariaLabel="Open availability actions menu" />
                  }
                >
                  <SDSMenuItem onClick={() => handleOpenModal(s.id)}>Edit</SDSMenuItem>
                  {!s.isPrimary && <SDSMenuItem onClick={() => onSetDefault(s.id)}>Set as Default</SDSMenuItem>}
                  <SDSMenuItem onClick={() => onDuplicate(s)}>Duplicate</SDSMenuItem>
                </SDSMenu>
              </SDSFlexbox>

              {/* Add horizontal separator between items (except after last item) */}
              {index < schedules.length - 1 && <SDSSeparator orientation="horizontal" />}
            </div>
          ))}
        </SDSFlexbox>
      </SDSFlexbox>

      {userId && <DateOverridesSection userId={userId} />}

      <AvailabilityEditModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        scheduleId={editingScheduleId}
        onSaveSuccess={handleScheduleSaveSuccess}
      />
    </>
  );
}
